import {
  Badge,
  Button,
  HStack,
  Icon,
  Stack,
  Table,
  Text,
} from '@chakra-ui/react';
import { FC } from 'react';
import { FiUser, FiUsers } from 'react-icons/fi';
import CollaboratorRoleSelect from './CollaboratorRoleSelect';
import {
  Collaborator,
  ProjectRoleValue,
  getCollaboratorKey,
  PROJECT_ROLE_OPTIONS,
} from '../types';

type CollaboratorsTableProps = {
  collaborators: Collaborator[];
  canManage: boolean;
  pendingRoleChanges: Set<string>;
  onRoleChange: (collaborator: Collaborator, role: ProjectRoleValue) => void;
  onRemove: (collaborator: Collaborator) => void;
};

const CollaboratorsTable: FC<CollaboratorsTableProps> = ({
  collaborators,
  canManage,
  pendingRoleChanges,
  onRoleChange,
  onRemove,
}) => {
  const hasRoleOptions = PROJECT_ROLE_OPTIONS.length > 0;

  return (
    <Table.ScrollArea>
      <Table.Root>
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeader>Collaborator</Table.ColumnHeader>
            <Table.ColumnHeader>Membership</Table.ColumnHeader>
            <Table.ColumnHeader>Contact / Notes</Table.ColumnHeader>
            <Table.ColumnHeader textAlign="center">
              Access level
            </Table.ColumnHeader>
            <Table.ColumnHeader textAlign="right">Actions</Table.ColumnHeader>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {collaborators.map((collaborator) => {
            const isTeam = Boolean(collaborator.team);
            const key = getCollaboratorKey(collaborator);
            const pending = pendingRoleChanges.has(key);

            const displayName = isTeam
              ? collaborator.team?.name ?? 'Unnamed team'
              : collaborator.user?.name ??
                collaborator.user?.email ??
                'Unknown user';

            const secondary = isTeam
              ? 'Team collaborator'
              : collaborator.user?.email ?? 'No email provided';

            return (
              <Table.Row key={collaborator.id}>
                <Table.Cell>
                  <HStack gap="2">
                    <Icon as={isTeam ? FiUsers : FiUser} />
                    <Stack gap="0.5">
                      <Text fontWeight="medium">{displayName}</Text>
                      <Text color="fg.muted" fontSize="sm">
                        {secondary}
                      </Text>
                    </Stack>
                  </HStack>
                </Table.Cell>
                <Table.Cell>
                  <Badge variant="subtle">
                    {isTeam ? 'Team' : 'Individual'}
                  </Badge>
                </Table.Cell>
                <Table.Cell>
                  <Text fontSize="sm" color="fg.muted">
                    {isTeam
                      ? 'All members of this team inherit access.'
                      : collaborator.user?.email ?? 'No email provided'}
                  </Text>
                </Table.Cell>
                <Table.Cell textAlign="center">
                  {canManage && hasRoleOptions ? (
                    <CollaboratorRoleSelect
                      value={collaborator.role}
                      onChange={(role) => {
                        if (role !== collaborator.role) {
                          onRoleChange(collaborator, role);
                        }
                      }}
                      disabled={pending}
                    />
                  ) : (
                    <Badge variant="outline" colorPalette="gray">
                      {collaborator.role.charAt(0) +
                        collaborator.role.slice(1).toLowerCase()}
                    </Badge>
                  )}
                </Table.Cell>
                <Table.Cell textAlign="right">
                  {canManage ? (
                    <Button
                      variant="outline"
                      colorPalette="red"
                      size="sm"
                      onClick={() => onRemove(collaborator)}
                    >
                      Remove
                    </Button>
                  ) : null}
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table.Root>
    </Table.ScrollArea>
  );
};

export default CollaboratorsTable;
